import { useMemo } from "react";
import LazyApexChart from '../../../client/components/LazyApexChart';
import type { ApexOptions } from "apexcharts";
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { chartColors, pnlColor } from "../chart/chartColors";
import ChartViewToggle from "../ChartViewToggle";
import WidgetDataTable from "../WidgetDataTable";
import type { WidgetViewProps } from "../types";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function fmt(v: number): string {
  const abs = Math.abs(v);
  const prefix = v < 0 ? "-$" : "$";
  if (abs >= 1000) return `${prefix}${(abs / 1000).toFixed(1)}k`;
  return `${prefix}${abs.toFixed(0)}`;
}

const MOOD_ORDER = ["excellent", "good", "neutral", "poor", "bad"];

export default function SessionMoodWidget({ viewMode = "chart", onSetViewMode }: WidgetViewProps) {
  const { analyticsData, isDark, themeId, t } = useDashboardData();

  const sessionStats = analyticsData?.sessionStats;

  const moodLabel = (mood: string): string => {
    const map: Record<string, string> = {
      excellent: t.analytics.sessionMoodExcellent,
      good: t.analytics.sessionMoodGood,
      neutral: t.analytics.sessionMoodNeutral,
      poor: t.analytics.sessionMoodPoor,
      bad: t.analytics.sessionMoodBad,
    };
    return map[mood] ?? mood;
  };

  const orderedMoods = useMemo(() => {
    if (!sessionStats) return [];
    return sessionStats.moodStats.slice().sort(
      (a, b) => MOOD_ORDER.indexOf(a.mood) - MOOD_ORDER.indexOf(b.mood),
    );
  }, [sessionStats]);

  const moodColors = orderedMoods.map((m) => pnlColor(m.avgPnl));

  const tableColumns = [
    { key: "mood", label: t.analytics.moodLabel, align: "left" as const },
    { key: "avgPnl", label: t.analytics.avgPnlLabel, align: "right" as const },
    { key: "sessions", label: t.analytics.sessionsCountLabel, align: "right" as const },
  ];
  const tableRows = orderedMoods.map((m) => ({
    mood: moodLabel(m.mood),
    avgPnl: (
      <span className={m.avgPnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"}>
        {m.avgPnl >= 0 ? "+" : ""}
        {m.avgPnl.toFixed(2)}
      </span>
    ),
    sessions: m.sessionCount,
  }));

  const c = chartColors();
  const gridColor = c.grid;
  const tooltipTheme = isDark ? "dark" : "light";
  const axisLabelStyle = { colors: c.axis, fontSize: "11px" };

  const moodOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: "bar" as const,
      toolbar: { show: false },
      background: "transparent",
      animations: { enabled: false },
    },
    plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "60%", distributed: true } },
    colors: moodColors,
    dataLabels: {
      enabled: true,
      formatter: fmt as any,
      // dark text reads on the emerald/red colored bars in every theme
      style: { fontSize: "11px", fontWeight: 700, colors: ["#0F1729"] },
      offsetX: 4,
    },
    grid: {
      borderColor: gridColor,
      strokeDashArray: 3,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
    },
    xaxis: {
      categories: orderedMoods.map((m) => moodLabel(m.mood)),
      labels: { style: axisLabelStyle, formatter: fmt as any },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: { style: { ...axisLabelStyle, fontSize: "12px", fontWeight: "600" } },
    },
    tooltip: { theme: tooltipTheme, y: { formatter: fmt as any } },
    legend: { show: false },
    theme: { mode: isDark ? "dark" as const : "light" as const },
  }), [isDark, orderedMoods, moodColors, gridColor, tooltipTheme]);

  if (!analyticsData) {
    return (
      <Card className="h-full">
        <CardContent className="h-full p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  if (!sessionStats?.hasEnoughData || orderedMoods.length === 0) {
    return (
      <Card className="h-full">
        <CardSectionHeader
          label={t.analytics.sessionsLabel ?? "Sessions"}
          title={t.analytics.sessionStatsMoodTitle}
          isDark={isDark}
          inline
        />
        <CardContent className="h-full p-4">
          <EmptyState label={t.analytics.notEnoughSessionData ?? t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardSectionHeader
        label={t.analytics.sessionsLabel ?? "Sessions"}
        title={t.analytics.sessionStatsMoodTitle}
        isDark={isDark}
        inline
        rightContent={<ChartViewToggle value={viewMode} onChange={onSetViewMode} />}
      />
      <CardContent className="px-5 pb-5 pt-1">
        {viewMode === "table" ? (
          <div style={{ height: Math.max(200, orderedMoods.length * 52 + 40) }}>
            <WidgetDataTable columns={tableColumns} rows={tableRows} emptyLabel={t.analytics.noDataAvailable} />
          </div>
        ) : (
          <LazyApexChart
            key={`session-mood-${themeId}`}
            options={moodOptions}
            series={[{ name: t.analytics.averagePnl, data: orderedMoods.map((m) => m.avgPnl) }]}
            type="bar"
            height={Math.max(200, orderedMoods.length * 52 + 40)}
          />
        )}
      </CardContent>
    </Card>
  );
}
